import React, { useMemo, useState } from 'react';
import {
  Button, Checkbox, Col, Form, Input, InputNumber, Row, Segmented, Select, Space, Switch, Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const WNUM_COUNT = 3;

function SectionTitle({ children }) {
  return (
    <Typography.Text
      type="secondary"
      style={{
        display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 0 8px',
      }}
    >
      {children}
    </Typography.Text>
  );
}

function RenderInputs({
  placeData, setPlaceData, zones, queues,
}) {
  const [search, setSearch] = useState('');

  const {
    connectedQueues, connectedZones, description, disabled, name, placename,
    mobile_wplace: mobileWplace,
  } = placeData;

  const update = (patch) => setPlaceData({ ...placeData, ...patch });

  // placename хранит номера для вызова через запятую: "WNUM0,WNUM1,WNUM2"
  const wnums = placename.replace(/\D+/ig, ',').replace(/^,/, '').split(',');
  const handleWnum = (index, value) => {
    const next = [...wnums];
    next[index] = value;
    update({ placename: next.filter(Boolean).join(',') });
  };

  const sortedQueues = useMemo(() => [...queues]
    .sort((a, b) => a.name.localeCompare(b.name, 'ru', { numeric: true })), [queues]);
  const query = search.trim().toLowerCase();
  const visibleQueues = query
    ? sortedQueues.filter((queue) => queue.name.toLowerCase().includes(query))
    : sortedQueues;

  const toggleQueue = (id, checked) => update({
    connectedQueues: checked
      ? [...connectedQueues, id]
      : connectedQueues.filter((queueId) => queueId !== id),
  });

  const selectVisible = () => {
    const ids = visibleQueues.map((queue) => queue.id);
    update({ connectedQueues: [...new Set([...connectedQueues, ...ids])] });
  };

  const clearVisible = () => {
    const ids = new Set(visibleQueues.map((queue) => queue.id));
    update({ connectedQueues: connectedQueues.filter((id) => !ids.has(id)) });
  };

  return (
    <Form layout="vertical" component="div" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <SectionTitle>Основное</SectionTitle>
      <Form.Item label="Наименование" required>
        <Input value={name} onChange={(e) => update({ name: e.target.value })} />
      </Form.Item>
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item label="Подключенная зона" required>
            <Select
              value={connectedZones[0]}
              placeholder="Выберите зону"
              showSearch
              optionFilterProp="children"
              onChange={(value) => update({ connectedZones: [Number(value)] })}
            >
              {zones.map((zone) => (
                <Select.Option key={zone.id} value={zone.id}>{zone.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Состояние">
            <Space>
              <Switch checked={!disabled} onChange={(checked) => update({ disabled: !checked })} />
              {disabled ? 'Отключено' : 'Включено'}
            </Space>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Тип рабочего места"
        extra={mobileWplace
          ? 'Для операторов, которые работают с планшета'
          : 'Обычное рабочее место оператора'}
      >
        <Segmented
          block
          options={[
            { label: 'Статичное', value: 'static' },
            { label: 'Мобильное', value: 'mobile' },
          ]}
          value={mobileWplace ? 'mobile' : 'static'}
          onChange={(value) => update({ mobile_wplace: value === 'mobile' })}
        />
      </Form.Item>
      <Form.Item label="Описание">
        <Input
          value={description}
          placeholder="Необязательно"
          onChange={(e) => update({ description: e.target.value })}
        />
      </Form.Item>

      <SectionTitle>Номер для вызова</SectionTitle>
      <Form.Item
        required
        extra="Номера, которые называются при вызове талона. Подставляются в звуковой шаблон вместо WNUM0, WNUM1, WNUM2: например, «в кабинет WNUM0» → «в кабинет 12»."
      >
        <Row gutter={8}>
          {Array.from({ length: WNUM_COUNT }, (_, index) => (
            <Col span={8} key={index}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                value={wnums[index]}
                placeholder={`WNUM${index}`}
                onChange={(value) => handleWnum(index, value)}
              />
            </Col>
          ))}
        </Row>
      </Form.Item>

      <SectionTitle>Услуги</SectionTitle>
      <div style={{
        display: 'flex', flexDirection: 'column', flex: 1, minHeight: 280,
      }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Поиск услуги"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Row justify="space-between" align="middle" style={{ margin: '8px 0' }}>
          <Typography.Text type="secondary">
            {`Выбрано ${connectedQueues.length} из ${queues.length}`}
          </Typography.Text>
          <Space size={0}>
            <Button type="link" size="small" onClick={selectVisible}>
              {query ? 'Выбрать найденные' : 'Выбрать все'}
            </Button>
            <Button type="link" size="small" onClick={clearVisible}>
              {query ? 'Снять найденные' : 'Снять все'}
            </Button>
          </Space>
        </Row>
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '4px 12px',
        }}
        >
          {visibleQueues.length ? visibleQueues.map((queue) => (
            <div key={queue.id} style={{ padding: '5px 0' }}>
              <Checkbox
                checked={connectedQueues.includes(queue.id)}
                onChange={(e) => toggleQueue(queue.id, e.target.checked)}
              >
                {queue.name}
              </Checkbox>
            </div>
          )) : (
            <Typography.Text type="secondary" style={{ display: 'block', padding: '8px 0' }}>
              Ничего не найдено
            </Typography.Text>
          )}
        </div>
      </div>
    </Form>
  );
}

export default RenderInputs;
